import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { GeneralSelect, GeneralInput, LocationHierarchyDropdown } from 'sharedComponents';
import { MAX_LENGTH_POWERTRAIN_NAME } from '../../../constants';
import './style.scss';

/**
 * BasicInfoStep - First step for collecting basic powertrain information
 * Handles powertrain name, site selection, and organization selection
 */
const BasicInfoStep = ({
    stepData = {},
    onStepDataChange = () => { },
    onStepComplete = () => { },
    onValidationChange = () => { }, // New prop to report validation status
    mode = 'create',
    isActive = false,
    sites = [],
    hierarchyData = null,
    organizationId = null
}) => {
    const { t: translate } = useTranslation();
    const [formData, setFormData] = useState({
        powertrainName: stepData.powertrainName || '',
        selectedSite: stepData.selectedSite || null,
        selectedLocation: stepData.selectedLocation || null
    });
    const [validationErrors, setValidationErrors] = useState({});
    const [touchedFields, setTouchedFields] = useState({}); // Track which fields have been touched
    const [showErrors, setShowErrors] = useState(false); // Track whether to show errors globally
    const lastSentData = useRef(null);
    const originalPowertrainNameLength = useRef(stepData.powertrainName?.length || 0);
    const isLocationManuallySelected = useRef(false);
    const isSiteManuallySelected = useRef(false);

    // Determine which fields are locked because they were pre-selected from the hierarchy panel
    // In edit mode, site is always locked unless manually overridden
    const isSiteLockedFromHierarchy = !isSiteManuallySelected.current && !!stepData.selectedSite;

    // Disabled check point: site select is disabled in edit mode or when pre-selected from hierarchy
    const isSiteSelectDisabled = mode === 'edit' && !isSiteManuallySelected.current || isSiteLockedFromHierarchy;

    // Use sites from props
    const availableSites = sites || [];

    // Memoize site options for better performance
    const siteOptions = useMemo(() => {
        return availableSites.map(site => ({
            value: site.siteId,
            label: site.siteName
        }));
    }, [availableSites]);

    // Memoize selected site value for GeneralSelect
    const selectedSiteValue = useMemo(() => {
        if (!formData.selectedSite) {
            return null;
        }
        return {
            value: formData.selectedSite.siteId,
            label: formData.selectedSite.siteName
        };
    }, [formData.selectedSite]);

    // Validation function
    const validateForm = useCallback(() => {
        const errors = {};

        // Check if site is selected
        if (!formData.selectedSite) {
            errors.selectedSite = translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_SiteRequired');
        }

        // Check if powertrain name is filled
        if (!formData.powertrainName || formData.powertrainName.trim() === '') {
            errors.powertrainName = translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_NameRequired');
        } else {
            // Check max length only in create mode OR if in edit mode and name length exceeds original
            const shouldCheckMaxLength = mode === 'create' ||
                mode === 'edit' && formData.powertrainName.length > originalPowertrainNameLength.current;

            if (shouldCheckMaxLength && formData.powertrainName.length > MAX_LENGTH_POWERTRAIN_NAME) {
                errors.powertrainName = translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_NameTooLong',
                    { maxLength: MAX_LENGTH_POWERTRAIN_NAME });
            }
        }

        return errors;
    }, [formData, mode, translate]);

    // Validate form and notify parent whenever form data changes
    useEffect(() => {
        const errors = validateForm();
        const isValid = Object.keys(errors).length === 0;

        setValidationErrors(errors);
        // Pass both validation status and whether user has interacted
        onValidationChange(isValid, showErrors);
    }, [formData, validateForm, onValidationChange, showErrors]);

    // Update parent when form data changes - but prevent duplicate calls
    useEffect(() => {
        // Convert formData to string for comparison to avoid object reference issues
        const currentDataString = JSON.stringify(formData);
        const lastDataString = lastSentData.current;

        // Only call onStepDataChange if the data actually changed
        if (currentDataString !== lastDataString) {
            onStepDataChange(formData);
            lastSentData.current = currentDataString;
        }
    }, [formData]); // Removed onStepDataChange from dependencies to prevent infinite loop

    const handleInputChange = useCallback((field, value) => {
        // Ensure powertrainName is always a string
        const sanitizedValue = field === 'powertrainName' ? value || '' : value;

        // Mark field as touched when user interacts with it
        setTouchedFields(prev => ({
            ...prev,
            [field]: true
        }));

        // Enable error display after first interaction
        setShowErrors(true);

        setFormData(prev => ({
            ...prev,
            [field]: sanitizedValue
        }));

        // Clear validation error for this field
        if (validationErrors[field]) {
            setValidationErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    }, [validationErrors]);

    const handleSiteChange = useCallback((selectedOption) => {
        isSiteManuallySelected.current = true;
        // Handle GeneralSelect onChange - can be null, value, or option object
        if (!selectedOption) {
            handleInputChange('selectedSite', null);
            handleInputChange('selectedLocation', null);
            return;
        }

        const siteId = typeof selectedOption === 'object' ? selectedOption.value : selectedOption;
        const selectedSite = availableSites.find(site => site.siteId === parseInt(siteId));
        handleInputChange('selectedSite', selectedSite || null);
        // Clear location when site changes
        handleInputChange('selectedLocation', null);
    }, [availableSites, handleInputChange]);

    const handleLocationChange = useCallback((locationData) => {
        isLocationManuallySelected.current = true;
        if (!locationData || !locationData.locationId) {
            handleInputChange('selectedLocation', null);
            return;
        }
        handleInputChange('selectedLocation', locationData);
    }, [handleInputChange]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        // Mark all fields as touched when submitting
        setTouchedFields({
            powertrainName: true,
            selectedSite: true,
            selectedLocation: true
        });

        onStepComplete(formData);
    }, [formData, onStepComplete]);

    return (
        <div className={`basic-info-step ${isActive ? 'active' : ''}`}>
            <form onSubmit={handleSubmit} className='step-form'>
                <div className='form-content-centered'>
                    {/* Powertrain Name */}
                    <div className='form-group'>
                        <label className='form-label'>
                            {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_PowertrainNameLabel')}
                        </label>
                        <GeneralInput
                            className='form-input'
                            id='powertrain-name'
                            placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_PowertrainNamePlaceholder')}
                            width='320px'
                            onChange={(value) => handleInputChange('powertrainName', value || '')}
                            value={formData?.powertrainName || ''}
                            maxLength={mode === 'create' ? MAX_LENGTH_POWERTRAIN_NAME : undefined}
                        />
                        {showErrors && touchedFields?.powertrainName && validationErrors?.powertrainName &&
                            <div className='error-message'>{validationErrors.powertrainName}</div>
                        }
                    </div>

                    {/* Site Selection */}
                    <div className='form-group'>
                        <label className='form-label'>
                            {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_SiteLabel')}
                        </label>
                        <GeneralSelect
                            className='form-select'
                            id={isSiteSelectDisabled ? 'site-select-disabled' : 'site-select'}
                            placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_SitePlaceholder')}
                            onChange={handleSiteChange}
                            value={selectedSiteValue}
                            options={siteOptions}
                            isSearchable={false}
                            isDisabled={isSiteSelectDisabled}
                        />
                        {showErrors && touchedFields?.selectedSite && validationErrors?.selectedSite &&
                            <div className='error-message'>{validationErrors.selectedSite}</div>
                        }
                    </div>

                    {/* Location Selection */}
                    <div className='form-group'>
                        <label className='form-label'>
                            {translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_LocationLabel')}
                        </label>
                        <LocationHierarchyDropdown
                            hierarchyData={hierarchyData}
                            selectedSite={formData?.selectedSite}
                            selectedLocation={formData?.selectedLocation}
                            organizationId={organizationId}
                            onSelect={handleLocationChange}
                            placeholder={translate('ABB.Powertrain.Frontend.ptToolsPowertrainWizard_BasicInfo_LocationPlaceholder')}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

BasicInfoStep.propTypes = {
    stepData: PropTypes.object,
    onStepDataChange: PropTypes.func,
    onStepComplete: PropTypes.func,
    onValidationChange: PropTypes.func,
    mode: PropTypes.oneOf(['create', 'edit']),
    isActive: PropTypes.bool,
    sites: PropTypes.arrayOf(PropTypes.shape({
        siteId: PropTypes.number,
        siteName: PropTypes.string
    })),
    hierarchyData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    organizationId: PropTypes.number
};

export default BasicInfoStep;
